import { useMemo, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Search, Trash, X as IconX, Trash2, Plus } from "lucide-react";

export function VendorSearchPicker({ query, setQuery, options, selectedId, onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Input
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        placeholder="Search vendor…"
        data-testid="purchase-vendor-existing-input"
      />
      {open && options.length > 0 && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {options.map((v) => (
            <button
              key={v.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPick(v); setOpen(false); }}
              className={`w-full px-3 py-2 text-left text-[12px] hover:bg-violet-50 flex items-center justify-between gap-2 border-b last:border-0 ${selectedId === v.id ? "bg-violet-50" : ""}`}
              data-testid={`purchase-vendor-opt-${v.id}`}
            >
              <span className="text-slate-800 font-medium">{v.vendor_name}</span>
              {v.is_pending && <Badge className="bg-amber-100 text-amber-800 text-[9px]">Pending</Badge>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function PurchaseLine({ idx, line, inventory, onUpdate, onRemove }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(line.inventory_label || line.custom_name || "");

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return inventory.slice(0, 8);
    return inventory.filter((i) => `${i.product_name} ${i.sku || ""}`.toLowerCase().includes(q)).slice(0, 8);
  }, [inventory, query]);

  const trimmed = query.trim();
  const exactMatch = trimmed ? inventory.find((i) => i.product_name.toLowerCase() === trimmed.toLowerCase()) : null;
  const canAddCustom = trimmed.length > 0 && !exactMatch;

  const pickInventory = (item) => {
    onUpdate({
      inventory_id: item.id,
      inventory_label: `${item.product_name}${item.sku ? ` · ${item.sku}` : ""}`,
      custom_name: "",
      purchase_price: line.purchase_price || Number(item.purchase_price || 0),
    });
    setQuery(`${item.product_name}${item.sku ? ` · ${item.sku}` : ""}`);
    setOpen(false);
  };
  const acceptCustom = () => {
    if (!canAddCustom) return;
    onUpdate({ inventory_id: "", inventory_label: "", custom_name: trimmed });
    setQuery(trimmed);
    setOpen(false);
  };

  const selling = line.purchase_price ? (Number(line.purchase_price) * 1.25).toFixed(2) : "0.00";

  return (
    <div className="p-3 border rounded-md bg-slate-50/60" data-testid={`purchase-line-${idx}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-600" data-testid={`purchase-line-sn-${idx}`}>
          <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-violet-600 text-white text-[11px] mono font-bold">
            {idx + 1}
          </span>
          <span className="text-slate-500">S/n {idx + 1}</span>
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_90px_120px_36px] gap-2 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Product</Label>
          <div className="relative">
            <Input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
                if (line.inventory_id || line.custom_name) onUpdate({ inventory_id: "", inventory_label: "", custom_name: "" });
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 180)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (suggestions.length > 0) pickInventory(suggestions[0]);
                  else if (canAddCustom) acceptCustom();
                }
              }}
              placeholder="Search or type product…"
              className="mt-1"
              data-testid={`purchase-line-input-${idx}`}
            />
            {open && (suggestions.length > 0 || canAddCustom) && (
              <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
                {suggestions.map((it) => (
                  <button
                    key={it.id} type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickInventory(it)}
                    className="w-full px-3 py-1.5 text-left text-[12px] hover:bg-violet-50 border-b last:border-0"
                    data-testid={`purchase-line-opt-${idx}-${it.id}`}
                  >
                    <span className="font-medium">{it.product_name}</span>
                    {it.sku && <span className="ml-2 mono text-[10px] text-slate-500">{it.sku}</span>}
                  </button>
                ))}
                {canAddCustom && (
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={acceptCustom}
                    className="w-full px-3 py-1.5 text-left text-[12px] hover:bg-amber-50 flex items-center gap-2"
                    data-testid={`purchase-line-custom-${idx}`}
                  >
                    <Plus size={11} className="text-amber-600" />
                    Add custom: <span className="font-semibold">&quot;{trimmed}&quot;</span>
                  </button>
                )}
              </div>
            )}
          </div>
          {line.custom_name && !line.inventory_id && (
            <span className="inline-block text-[9px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded mt-1">
              Pending Inventory
            </span>
          )}
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Qty</Label>
          <Input
            type="number" min={1} step={1}
            value={line.qty_purchased}
            onChange={(e) => onUpdate({ qty_purchased: e.target.value })}
            className="mt-1 text-center mono"
            data-testid={`purchase-line-qty-${idx}`}
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Purchase ₹</Label>
          <Input
            type="number" min={0} step="0.01"
            value={line.purchase_price}
            onChange={(e) => onUpdate({ purchase_price: e.target.value })}
            className="mt-1 mono"
            data-testid={`purchase-line-price-${idx}`}
          />
        </div>
        <button type="button" onClick={onRemove} className="h-9 w-9 inline-flex items-center justify-center text-rose-600 hover:bg-rose-50 rounded mb-0.5" data-testid={`purchase-line-rm-${idx}`}>
          <Trash size={14} />
        </button>
      </div>
      <div className="mt-2 text-[11px] text-slate-600 flex items-center justify-between">
        <span>
          Selling auto-set to <span className="mono font-semibold text-emerald-700">₹ {selling}</span> (purchase + 25%)
        </span>
        <span className="mono font-semibold text-slate-800">
          Line: ₹ {(Number(line.qty_purchased || 0) * Number(line.purchase_price || 0)).toFixed(2)}
        </span>
      </div>
    </div>
  );
}
